import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { eventBus, EventNames } from '../utils/eventBus';
import { STORAGE_KEY } from '../constants/driveConstants';
import logger from '../utils/simpleLogger';

interface UseDriveSelectionProps {
  selectedDocuments?: string[];
  onDocumentSelect?: (documentIds: string[]) => void;
  // projectId removed - project system eliminated
}

export const useDriveSelection = ({
  selectedDocuments,
  onDocumentSelect,
}: UseDriveSelectionProps) => {
  const [internalSelectedDocuments, setInternalSelectedDocuments] = useState<string[]>(
    selectedDocuments ?? []
  );

  const selectionStorageKey = useMemo(
    () => `${STORAGE_KEY}:selection:global`, // projectId removed
    []
  );

  const hasRestoredSelectionRef = useRef(false);
  const pendingInternalUpdateRef = useRef<string[] | null>(null);
  const lastProcessedSelectedDocumentsRef = useRef<string[] | undefined>(undefined);
  const currentInternalSelectionRef = useRef<string[]>(selectedDocuments ?? []);

  // Reset refs when storage key changes
  useEffect(() => {
    hasRestoredSelectionRef.current = false;
    pendingInternalUpdateRef.current = null;
  }, [selectionStorageKey]);

  // Mark as restored to prevent localStorage restoration on remount
  // Note: localStorage restoration was disabled to avoid overwriting context selection
  // when DrivePanel remounts due to parent re-renders
  useEffect(() => {
    if (typeof window === 'undefined' || hasRestoredSelectionRef.current) {
      return;
    }
    hasRestoredSelectionRef.current = true;
  }, [selectionStorageKey]);

  // Sync internal selection with prop when it changes
  useEffect(() => {
    if (selectedDocuments === undefined) {
      return;
    }

    logger.debug('🔄 [useDriveSelection] selectedDocuments prop changed', {
      newSelection: selectedDocuments,
      newLength: selectedDocuments.length,
      currentInternal: currentInternalSelectionRef.current,
      pending: pendingInternalUpdateRef.current,
      lastProcessed: lastProcessedSelectedDocumentsRef.current
    });

    // Avoid loop: if this change comes from an internal update we just made, ignore it
    if (pendingInternalUpdateRef.current !== null) {
      const pending = pendingInternalUpdateRef.current;
      if (
        pending.length === selectedDocuments.length &&
        pending.every((id, idx) => id === selectedDocuments[idx])
      ) {
        // Same value we just set, clear ref and do nothing
        logger.debug('⏭️ [useDriveSelection] Skipping - same as pending update', { pending });
        pendingInternalUpdateRef.current = null;
        lastProcessedSelectedDocumentsRef.current = selectedDocuments;
        currentInternalSelectionRef.current = selectedDocuments;
        return;
      }
      // If it doesn't match, parent updated it for another reason, process it
      logger.debug('🔄 [useDriveSelection] Pending update doesn\'t match, processing', {
        pending,
        selectedDocuments
      });
      pendingInternalUpdateRef.current = null;
    }

    // Compare with current internal state using ref (faster than accessing state)
    const currentInternal = currentInternalSelectionRef.current;

    // Special case: if clearing to empty array, always update to ensure sync
    const isClearing = selectedDocuments.length === 0 && currentInternal.length > 0;

    if (
      !isClearing &&
      currentInternal.length === selectedDocuments.length &&
      currentInternal.every((id, idx) => id === selectedDocuments[idx])
    ) {
      // Value already matches internal state, do nothing
      logger.debug('⏭️ [useDriveSelection] Skipping - already matches internal state', {
        currentInternal
      });
      lastProcessedSelectedDocumentsRef.current = selectedDocuments;
      return;
    }

    if (isClearing) {
      logger.info('🧹 [useDriveSelection] Clearing selection - forcing update', {
        from: currentInternal,
        to: selectedDocuments
      });
    }

    // Compare with last processed value to avoid loops
    // Skip this check if we're clearing (going from non-empty to empty)
    if (!isClearing && lastProcessedSelectedDocumentsRef.current !== undefined) {
      const lastProcessed = lastProcessedSelectedDocumentsRef.current;
      if (
        lastProcessed.length === selectedDocuments.length &&
        lastProcessed.every((id, idx) => id === selectedDocuments[idx])
      ) {
        // Value hasn't changed since last time we processed it, do nothing
        logger.debug('⏭️ [useDriveSelection] Skipping - same as last processed', {
          lastProcessed
        });
        return;
      }
    }

    // Only update if really different
    logger.info('✅ [useDriveSelection] Updating internal selection', {
      from: currentInternalSelectionRef.current,
      to: selectedDocuments,
      fromLength: currentInternalSelectionRef.current.length,
      toLength: selectedDocuments.length,
      isClearing
    });
    lastProcessedSelectedDocumentsRef.current = selectedDocuments;
    currentInternalSelectionRef.current = selectedDocuments;
    hasRestoredSelectionRef.current = true;
    setInternalSelectedDocuments(selectedDocuments);

    // If clearing, also clear localStorage immediately to prevent restoration
    if (isClearing && typeof window !== 'undefined') {
      try {
        window.localStorage.removeItem(selectionStorageKey);
        logger.info('🧹 [useDriveSelection] Cleared localStorage', { selectionStorageKey });
      } catch (error) {
        console.warn('No se pudo limpiar localStorage', error);
      }
    }
  }, [selectedDocuments, selectionStorageKey]);

  // Persist current selection in localStorage
  useEffect(() => {
    if (typeof window === 'undefined') {
      return;
    }

    try {
      window.localStorage.setItem(selectionStorageKey, JSON.stringify(internalSelectedDocuments));
    } catch (error) {
      console.warn('No se pudo guardar la selección del drive en localStorage', error);
    }
  }, [internalSelectedDocuments, selectionStorageKey]);

  // Listen for auto-selected documents from chat
  useEffect(() => {
    const handleAutoSelectedDocuments = (data: { documentIds: string[] }) => {
      if (data.documentIds && data.documentIds.length > 0) {
        // Diferir la actualización para evitar bloquear el hilo principal durante clicks
        requestAnimationFrame(() => {
          // Check if the selection actually changed to avoid unnecessary updates
          const currentSelection = currentInternalSelectionRef.current;
          const newSelection = data.documentIds;

          const hasChanged =
            currentSelection.length !== newSelection.length ||
            !currentSelection.every((id, idx) => id === newSelection[idx]);

          if (!hasChanged) {
            // No change, skip update
            return;
          }

          // Mark that we're updating from external action (event)
          pendingInternalUpdateRef.current = newSelection;
          // Update refs
          lastProcessedSelectedDocumentsRef.current = newSelection;
          currentInternalSelectionRef.current = newSelection;
          // Update internal selection - this will trigger re-render
          setInternalSelectedDocuments(newSelection);
          // Notify parent component after state update to avoid
          // "Cannot update component while rendering" warning
          queueMicrotask(() => {
            onDocumentSelect?.(newSelection);
          });
          logger.info(
            `✅ Drive: ${newSelection.length} documentos auto-seleccionados y resaltados en tiempo real`
          );
        });
      }
    };

    eventBus.on(EventNames.DOCUMENTS_AUTO_SELECTED, handleAutoSelectedDocuments);

    return () => {
      eventBus.off(EventNames.DOCUMENTS_AUTO_SELECTED, handleAutoSelectedDocuments);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only mount once, onDocumentSelect accessed from closure

  const handleDocumentToggle = useCallback(
    (documentId: string) => {
      // Calculate new selection and update state
      // IMPORTANT: Don't call onDocumentSelect inside setState updater to avoid
      // "Cannot update component while rendering" warning
      let newSelection: string[] = [];
      setInternalSelectedDocuments((prev) => {
        const isSelected = prev.includes(documentId);
        newSelection = isSelected
          ? prev.filter((id) => id !== documentId)
          : [...prev, documentId];
        currentInternalSelectionRef.current = newSelection;
        pendingInternalUpdateRef.current = newSelection;
        return newSelection;
      });
      // Notify parent after setState is complete
      // Using queueMicrotask ensures this runs after the current render cycle
      queueMicrotask(() => {
        onDocumentSelect?.(newSelection);
      });
    },
    [onDocumentSelect]
  );

  const handleSelectAll = useCallback(
    (documentIds: string[]) => {
      setInternalSelectedDocuments(documentIds);
      currentInternalSelectionRef.current = documentIds;
      pendingInternalUpdateRef.current = documentIds;
      // Notify parent after state update
      queueMicrotask(() => {
        onDocumentSelect?.(documentIds);
      });
    },
    [onDocumentSelect]
  );

  const handleDeselectAll = useCallback(() => {
    setInternalSelectedDocuments([]);
    currentInternalSelectionRef.current = [];
    pendingInternalUpdateRef.current = [];
    // Notify parent after state update
    queueMicrotask(() => {
      onDocumentSelect?.([]);
    });
  }, [onDocumentSelect]);

  return {
    internalSelectedDocuments,
    setInternalSelectedDocuments,
    handleDocumentToggle,
    handleSelectAll,
    handleDeselectAll,
  };
};



